// useAds.ts
/*
//広告情報を扱うサービス
// 取得した情報はIndexedDBにキャッシュし、オフラインにも対応します。
*/

import useSWR, { mutate } from 'swr';
import { collection, getDocs } from 'firebase/firestore';
import { appId, db } from '../firebase';
import { useUserProfile } from '../hooks/useUserProfile';
import { Ad } from '../types/index';
import { saveAdsToDB, loadAdsFromDB } from '../lib/adsIdbHelper'; // 修正されたヘルパーをインポート
import { logger } from '../utils/logger';

/**
 * FirestoreまたはIndexedDBキャッシュから広告を取得する
 * (SWRのfetcherとして機能)
 * @returns {Promise<Ad[]>} - 取得した広告データの配列
 */
const fetchAdsWithCache = async (): Promise<Ad[]> => {
    // まずローカルのキャッシュを試す
    const cachedAds = await loadAdsFromDB();
  
    // 有効なキャッシュがあればそれを即座に返す
    if (cachedAds) {
        logger.log("Using valid ad cache from IndexedDB.");
        return cachedAds;
    }
    
    // オンラインでない場合、キャッシュもなければ空を返す
    if (!navigator.onLine) {
        logger.log("Offline and no valid cache available.");
        return [];
    }

    // キャッシュがない、または期限切れの場合、Firestoreから取得
    try {
        logger.log("Fetching fresh ad data from Firestore...");
        const adsRef = collection(db, `artifacts/${appId}/public/data/ads`);
        const snapshot = await getDocs(adsRef);
        const freshAds: Ad[] = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() as Ad }));
        
        // Firestoreから取得したデータをDBに保存（非同期でOK）
        saveAdsToDB(freshAds); 
        
        return freshAds;
    } catch (error) {
        logger.error("Failed to fetch ads from Firestore.", error);
        // Firestoreからの取得に失敗した場合は空の配列を返す
        return [];
    }
};

/**
 * ユーザーの誕生日から年齢を算出
 * @param birthday - ユーザーの誕生日文字列
 * @returns 年齢、またはnull
 */
const calculateAge = (birthday: string | undefined): number | null => {
    if (!birthday) return null;
    const birthDate = new Date(birthday);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) age--;
    return age;
};

/**
 * SWRとIndexedDBを併用した、広告取得のためのカスタムフック
 * @param userId - ユーザーID
 * @returns フィルタリングされた広告、ローディング状態、エラー
 */
export const useAdsForUser = (userId: string | undefined) => {
    const { profile, isLoading: isProfileLoading, error: userProfileError } = useUserProfile(userId);

    // SWRで広告データを取得 (オフライン対応済みのfetcherを利用)
    const { data: ads, error: adsError, isLoading: isAdsLoading } = useSWR<Ad[]>(
        appId ? ['ads-cache', appId] : null, // SWRキーを少し変更
        fetchAdsWithCache,
        {
            revalidateOnFocus: true, // オンライン復帰時に自動で再検証
            shouldRetryOnError: true, // エラー時にリトライ
        }
    );

    const isLoading = isProfileLoading || (isAdsLoading && !ads); // データがまだない場合にのみローディング
    const error = userProfileError || adsError;

    if (isLoading) {
        return { ads: null, isLoading: true, error: undefined };
    }
    if (error && !ads) {
        return { ads: null, isLoading: false, error: error };
    }

    const displayAds = ads || [];
    if (displayAds.length === 0) {
        return { ads: [], isLoading: false, error: error };
    }

    const age = calculateAge(profile?.birthday);

    const filtered = displayAds.filter(ad => {
        // 年齢フィルタリングがない広告は常に表示
        if (typeof ad.age_from !== 'number' || typeof ad.age_to !== 'number') return true;
        // ユーザーの年齢が不明な場合は年齢フィルタリングを無視
        if (age === null) return true;
        return ad.age_from <= age && age <= ad.age_to;
    });

    const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;
    const count = isMobile ? 1 : 3;
    const shuffled = [...filtered].sort(() => Math.random() - 0.5);
    const selected = shuffled.slice(0, count);

    return { ads: selected, isLoading: false, error: error };
};